import { ReactElement } from 'react';

import { PreviewPageMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { DEFAULT_DOCUMENT_ALLOWED_FILE_TYPES } from 'dashboard/lib/stubs/verification-template-version';
import { InquiryTemplateVersionCombinedStep } from 'dashboard/models/inquiry-template-version';
import { BaseStepStyleProvider } from 'inquiry/context/BaseStepStyleContext';
import { StepDocumentStyleProvider } from 'inquiry/context/StepDocumentStyleContext';
import { StepGovernmentIdStyleProvider } from 'inquiry/context/StepGovernmentIdStyleContext';
import { StepSelfieStyleProvider } from 'inquiry/context/StepSelfieStyleContext';
import {
  createMockDynamicFlowInquiry,
  createMockIdClassesForCountry,
} from 'inquiry/lib/stubs/inquiry';
import { UiStatus } from 'inquiry/redux/actions/ui';
import { AutoclassificationResponseType } from 'inquiry/redux/thunk-actions/verification';
import InquiryDeviceHandoffEmailPromptView from 'inquiry/views/InquiryDeviceHandoffEmailPromptView';
import InquiryDeviceHandoffEmailSentView from 'inquiry/views/InquiryDeviceHandoffEmailSentView';
import InquiryDeviceHandoffPromptView from 'inquiry/views/InquiryDeviceHandoffPromptView';
import InquiryDeviceHandoffSmsPromptView from 'inquiry/views/InquiryDeviceHandoffSmsPromptView';
import InquiryDeviceHandoffSmsSentView from 'inquiry/views/InquiryDeviceHandoffSmsSentView';
import InquiryDeviceHandoffStartedView from 'inquiry/views/InquiryDeviceHandoffStartedView';
import InquiryDocumentsCaptureView from 'inquiry/views/InquiryDocumentsCaptureView';
import InquiryDocumentsCheckView from 'inquiry/views/InquiryDocumentsCheckView';
import InquiryDocumentsPendingView from 'inquiry/views/InquiryDocumentsPendingView';
import InquiryDocumentsPromptView from 'inquiry/views/InquiryDocumentsPromptView';
import InquiryGovernmentIdAutoclassificationFailureView from 'inquiry/views/InquiryGovernmentIdAutoclassificationFailureView';
import InquiryGovernmentIdAutoclassificationSelectView from 'inquiry/views/InquiryGovernmentIdAutoclassificationSelectView';
import InquiryGovernmentIdCaptureView from 'inquiry/views/InquiryGovernmentIdCaptureView';
import InquiryGovernmentIdCaptureViewRedesign from 'inquiry/views/InquiryGovernmentIdCaptureView/InquiryGovernmentIdCaptureViewRedesign';
import InquiryGovernmentIdCheckView from 'inquiry/views/InquiryGovernmentIdCheckView';
import InquiryGovernmentIdCheckViewRedesign from 'inquiry/views/InquiryGovernmentIdCheckView/InquiryGovernmentIdCheckViewRedesign';
import InquiryGovernmentIdPendingView from 'inquiry/views/InquiryGovernmentIdPendingView';
import InquiryGovernmentIdPromptView from 'inquiry/views/InquiryGovernmentIdPromptView';
import InquiryGovernmentIdSelectView from 'inquiry/views/InquiryGovernmentIdSelectView';
import InquiryIntegrationView from 'inquiry/views/InquiryIntegrationView';
import InquirySelfiePromptView from 'inquiry/views/InquirySelfiePromptView';
import InquirySelfieRecordView from 'inquiry/views/InquirySelfieRecordView';
import InquirySelfieReflectionPromptView from 'inquiry/views/InquirySelfieReflectionPromptView';
import InquiryUiView from 'inquiry/views/InquiryUiView';
import { GOVERNMENT_ID_DESIGN_VERSION } from 'lib/constants/governmentIds';
import DeviceHandoffOption from 'lib/interfaces/DeviceHandoffOption';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import {
  CombinedStepClientData,
  isCombinedStep,
  StepDocument,
  StepGovernmentId,
  StepIntegration,
  StepPreview,
  StepSelfie,
  StepSelfieReflection,
  StepUi,
  StepVerifyWithPersona,
  UiStepType,
} from 'lib/interfaces/StepsConfig';
import {
  AutoCaptureRuleType,
  GovernmentIdSide,
  RequiredDevice,
  SelfieMatch,
} from 'lib/interfaces/verificationConfig';
import { assertNever } from 'lib/utils/types';
import { SelfieStep } from 'verify/lib/interfaces/selfie';
import { CaptureMethod } from 'verify/models/verification';
import { DETECT_TIMEOUT } from 'verify/scanners/SelfieScanner/client';

/**
 * Note: the two redesign string values should match up with the strings
 * appended to the step_name used as the component key in the generatePages
 * method in dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/pages
 */
export function isFullscreenPage(page: ReactElement | undefined) {
  const key = page?.key?.toString();
  return !!(key?.includes('capture-page-redesign') || key?.includes('check-page-redesign'));
}

/**
 * @param stepsPreview client data representation of ITV steps
 * @param featureFlagCheck flags passed in for up-to-update previews
 * @returns element used to display live preview of step in editor within tree and screen editors
 */
export function generatePages(
  stepsPreview: StepPreview[],
  withDeviceHandoff = true,
  featureFlagCheck: FeatureFlagsMap = {},
): PreviewPageMap {
  // When applicable, insert the device handoff pages only once right after the first combined step,
  // enable to fetch the styles for device handoff's preview pages, we'd need to fetch the styles
  // of the first encountered combined step
  let isWithDeviceHandOffInserted = false;
  return stepsPreview.filter(Boolean).reduce((map, stepPreview) => {
    if (withDeviceHandoff && !isWithDeviceHandOffInserted && isCombinedStep(stepPreview)) {
      isWithDeviceHandOffInserted = true;
      return {
        ...map,
        [stepPreview.name]: generatePage(stepPreview, featureFlagCheck),
        'device-handoff': deviceHandoffPages({ stepsPreview }),
      };
    } else {
      return {
        ...map,
        [stepPreview.name]: generatePage(stepPreview, featureFlagCheck),
      };
    }
  }, {});
}

export function generatePage(
  stepPreview: StepPreview,
  featureFlagCheck: FeatureFlagsMap = {},
): ReactElement[] {
  switch (stepPreview.type) {
    case UiStepType.Complete:
      return [];
    case UiStepType.Ui:
      return [uiStepPage(stepPreview)];
    case UiStepType.Integration:
      return [integrationStepPage(stepPreview)];
    case UiStepType.VerifyWithPersona:
      return verifyWithPersonaStepPages(stepPreview);
    case UiStepType.GovernmentId:
      return governmentIdStepPages(stepPreview, featureFlagCheck);
    case UiStepType.Selfie:
      return selfieStepPages(stepPreview);
    case UiStepType.SelfieReflection:
      return selfieReflectionStepPages(stepPreview);
    case UiStepType.Document:
      return documentStepPages(stepPreview);
    case UiStepType.Loop:
      return generatePage(stepPreview.config.steps[0], featureFlagCheck);
    default:
      assertNever(stepPreview);
  }
}

/**
 * @deprecated
 */
function uiStepPage(stepPreview: StepUi): ReactElement {
  const mockAsyncFunction = () => Promise.resolve();
  const mockInquiry = createMockDynamicFlowInquiry({
    'next-step': stepPreview,
    'redirect-uri': 'www.withpersona.com', // pass mock redirect-uri, so button complete is visible
  });
  return (
    <InquiryUiView
      key={stepPreview.name}
      inquiry={mockInquiry}
      uiStatus={UiStatus.Ready}
      handleLanguageChange={mockAsyncFunction}
      showMessage={mockAsyncFunction}
      onTransitionSubmit={mockAsyncFunction}
      onTransitionAction={mockAsyncFunction}
      onComplete={mockAsyncFunction}
      onCancel={mockAsyncFunction}
      route={mockAsyncFunction}
      setComponent={mockAsyncFunction}
    />
  );
}

function integrationStepPage(stepPreview: StepIntegration): ReactElement {
  const mockFunction = () => {};
  const { config } = stepPreview;
  return (
    <InquiryIntegrationView
      key={stepPreview.name}
      config={config}
      handleContinue={mockFunction}
      previewMode={true}
    />
  );
}

export const combinedStepPagePrefix = (
  step: InquiryTemplateVersionCombinedStep | CombinedStepClientData,
): string => `${step.name}-`;

function governmentIdStepPages(
  stepGovernmentId: StepGovernmentId,
  featureFlagCheck: FeatureFlagsMap = {},
): ReactElement[] {
  const mockFunction = () => {};
  const mockAsyncFunction = () => Promise.resolve();
  const { config, styles } = stepGovernmentId;
  const useGovIdRedesign =
    stepGovernmentId.config['govid-design-version'] === GOVERNMENT_ID_DESIGN_VERSION.V1;
  const pages = [];
  // If autoclassification is enabled, leave id class as null, otherwise:
  //  Use first enabled id class for preview.
  //  We need a fallback because `idclasses` could be empty. This can happen if a locale
  //  is used for the steps preview and we do not have an associated config for enabled id classes
  //  for that country. Most commonly, this would happen if 'us' was removed from enabled id classes
  const idClass = config?.['auto-classification-config']?.['is-enabled']
    ? null
    : config.idclasses[0] || {
        class: 'dl',
        'requires-device': RequiredDevice.None,
        'requires-sides': [GovernmentIdSide.Front],
      };
  const currentSide = idClass?.['requires-sides']?.[0] || GovernmentIdSide.Front;
  // Similar to idClass, use first enabled id class.
  // Microblink is disabled for FLE previews regardless
  const autoCaptureConfig = config.idclasses[0]?.['capture-page-configs']?.[0]?.[
    'auto-capture-config'
  ] || {
    'rule-sets': [
      {
        rules: [
          {
            'is-required': false,
            type: AutoCaptureRuleType.IdFront,
            options: {},
          },
        ],
      },
    ],
  };

  if (!config?.['auto-classification-config']?.['is-enabled']) {
    pages.push(
      <StepGovernmentIdStyleProvider
        value={styles}
        key={`${combinedStepPagePrefix(stepGovernmentId)}select-page`}
      >
        <InquiryGovernmentIdSelectView
          assets={config.assets['select-page']}
          disclaimer={config.disclaimer}
          idClasses={config.idclasses}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          idClassListStyle={config['idclass-list-style']}
          localizations={config.localizations['select-page']}
          selectedCountryCode={config['selected-country-code']}
          enableRedesign={useGovIdRedesign}
          handleSelectType={mockFunction}
        />
      </StepGovernmentIdStyleProvider>,
    );
  }

  pages.push(
    <StepGovernmentIdStyleProvider
      value={styles}
      key={`${combinedStepPagePrefix(stepGovernmentId)}prompt-page`}
    >
      <InquiryGovernmentIdPromptView
        key={`${combinedStepPagePrefix(stepGovernmentId)}prompt-page`}
        assets={config.assets['prompt-page']}
        allowedCaptureOptions={config['enabled-capture-options-desktop']}
        allowedFileTypes={config['enabled-file-types']}
        currentSide={currentSide}
        governmentIdClass={idClass?.class}
        localizations={config.localizations['prompt-page']}
        selectedCountryCode={config['selected-country-code']}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        isProcessing={undefined}
        showCameraPermissionsDialog={false}
        skipPromptPage={false}
        handleDeviceHandoff={mockFunction}
        handleDrop={mockFunction}
        handleInputCapture={mockFunction}
        handleSelectCamera={mockAsyncFunction}
        hideCameraPermissionsDialog={mockFunction}
        openCameraPermissionsDialog={mockFunction}
      />
    </StepGovernmentIdStyleProvider>,
  );

  if (useGovIdRedesign) {
    pages.push(
      <StepGovernmentIdStyleProvider
        value={styles}
        key={`${combinedStepPagePrefix(stepGovernmentId)}capture-page-redesign`}
      >
        <InquiryGovernmentIdCaptureViewRedesign
          assets={config.assets['capture-page']}
          barcodeCameraManualCaptureDelayMs={config['barcode-camera-manual-capture-delay-ms']}
          cameraSelectOptions={[]}
          featureFlags={{}}
          governmentIdClass={idClass?.class}
          autoCaptureConfig={autoCaptureConfig}
          localizations={config.localizations['capture-page']}
          requiresSide={currentSide}
          videoConstraints={{ deviceId: { exact: 'fake' } }} // disable webcam preview
          webCameraManualCaptureDelayMs={config['web-camera-manual-capture-delay-ms']}
          isRetake={false}
          shouldDisplayCameraSelector={false}
          shouldUseMicroblink={false}
          uiReady={true}
          videoReady={true}
          handleCameraChange={mockFunction}
          handleCapture={mockAsyncFunction}
          handleError={mockFunction}
        />
      </StepGovernmentIdStyleProvider>,
      <StepGovernmentIdStyleProvider
        value={styles}
        key={`${combinedStepPagePrefix(stepGovernmentId)}check-page-redesign`}
      >
        <InquiryGovernmentIdCheckViewRedesign
          assets={config.assets['capture-page']}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          currentUpload={{ filename: 'id_filename', fileType: undefined, dataUrl: undefined }}
          featureFlags={featureFlagCheck}
          governmentIdClass={idClass?.class}
          localizations={config.localizations['check-page']}
          requiresSide={currentSide}
          reverseSide={GovernmentIdSide.Back}
          collapsed={false}
          flipped={false}
          isProcessing={false}
          handleContinue={mockFunction}
          handleRetry={mockFunction}
        />
      </StepGovernmentIdStyleProvider>,
    );
  } else {
    // NOTE: InquiryGovernmentIdCaptureView does NOT support SST because it is never used on govid-design-version === 1.
    // InquiryGovernmentIdCheckView is still used for the file upload flow, so does support SST.
    pages.push(
      <InquiryGovernmentIdCaptureView
        key={`${combinedStepPagePrefix(stepGovernmentId)}capture-page`}
        barcodeCameraManualCaptureDelayMs={config['barcode-camera-manual-capture-delay-ms']}
        featureFlags={featureFlagCheck}
        governmentIdClass={idClass?.class}
        localizations={config.localizations['capture-page']}
        requiresSide={currentSide}
        selectedCountryCode={config['selected-country-code']}
        videoConstraints={{ deviceId: { exact: 'fake' } }} // disable webcam preview
        webCameraManualCaptureDelayMs={config['web-camera-manual-capture-delay-ms']}
        zoomFactor={1}
        shouldUseMicroblink={false}
        handleCapture={mockAsyncFunction}
        handleError={mockFunction}
      />,
      <StepGovernmentIdStyleProvider
        value={styles}
        key={`${combinedStepPagePrefix(stepGovernmentId)}check-page`}
      >
        <InquiryGovernmentIdCheckView
          assets={config.assets['check-page']}
          localizations={config.localizations['check-page']}
          currentUpload={{ filename: 'id_filename', fileType: 'image/jpeg', dataUrl: '' }}
          isProcessing={false}
          handleContinue={mockFunction}
          handleRetry={mockFunction}
        />
      </StepGovernmentIdStyleProvider>,
    );
  }

  if (config?.['auto-classification-config']?.['is-enabled']) {
    pages.push(
      <StepGovernmentIdStyleProvider
        value={styles}
        key={`${combinedStepPagePrefix(stepGovernmentId)}auto-classification-failure-page`}
      >
        <InquiryGovernmentIdAutoclassificationFailureView
          localizations={config.localizations['auto-classification-page']}
          autoclassificationFailureReason={AutoclassificationResponseType.REJECTED}
          handleContinue={mockFunction}
        />
      </StepGovernmentIdStyleProvider>,
      <StepGovernmentIdStyleProvider
        value={styles}
        key={`${combinedStepPagePrefix(stepGovernmentId)}auto-classification-select-page`}
      >
        <InquiryGovernmentIdAutoclassificationSelectView
          localizations={config.localizations['auto-classification-page']}
          governmentIdClassLocalizations={config.localizations['select-page']}
          idClassesByCountries={createMockIdClassesForCountry()}
          selectIdTypeAndContinue={mockFunction}
          defaultCountry='us'
        />
      </StepGovernmentIdStyleProvider>,
    );
  }

  pages.push(
    <StepGovernmentIdStyleProvider
      value={styles}
      key={`${combinedStepPagePrefix(stepGovernmentId)}pending-page`}
    >
      <InquiryGovernmentIdPendingView
        assets={config.assets['pending-page']}
        localizations={config.localizations['pending-page']}
        textVerticalPosition={config['pending-page-text-vertical-position']}
      />
    </StepGovernmentIdStyleProvider>,
  );

  return pages;
}

function verifyWithPersonaStepPages(stepVerifyWithPersona: StepVerifyWithPersona): ReactElement[] {
  const mockFunction = () => {};
  const mockAsyncFunction = () => Promise.resolve();
  const mockInquiry = createMockDynamicFlowInquiry({
    'next-step': stepVerifyWithPersona,
    'redirect-uri': 'www.withpersona.com', // pass mock redirect-uri, so button complete is visible
  });
  return [
    <InquiryUiView
      key={`${stepVerifyWithPersona.name}-passkey-authentication-page`}
      nextStep={stepVerifyWithPersona.config.pages['passkey-authentication']['ui-step']}
      inquiry={mockInquiry}
      uiStatus={UiStatus.Ready}
      handleLanguageChange={mockFunction}
      showMessage={mockFunction}
      onTransitionSubmit={mockAsyncFunction}
      onTransitionAction={mockAsyncFunction}
      onComplete={mockFunction}
      onCancel={mockFunction}
      route={mockFunction}
      setComponent={mockFunction}
    />,
    <InquiryUiView
      key={`${stepVerifyWithPersona.name}-success-page`}
      nextStep={stepVerifyWithPersona.config.pages['success']['ui-step']}
      inquiry={mockInquiry}
      uiStatus={UiStatus.Ready}
      handleLanguageChange={mockFunction}
      showMessage={mockFunction}
      onTransitionSubmit={mockAsyncFunction}
      onTransitionAction={mockAsyncFunction}
      onComplete={mockFunction}
      onCancel={mockFunction}
      route={mockFunction}
      setComponent={mockFunction}
    />,
  ];
}

function selfieStepPages(stepSelfie: StepSelfie): ReactElement[] {
  const mockFunction = () => {};
  const mockAsyncFunction = () => Promise.resolve();
  const { config, styles } = stepSelfie;
  const selfieMatch = config['selfie-type'] || SelfieMatch.ProfileAndCenter;
  return [
    <StepSelfieStyleProvider
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      value={styles}
      key={`${combinedStepPagePrefix(stepSelfie)}prompt-page`}
    >
      <InquirySelfiePromptView
        assets={config.assets['prompt-page']}
        localizations={config.localizations['prompt-page']}
        handleSelectImage={mockFunction}
        handleStartRecording={mockAsyncFunction}
        handleDeviceHandoff={mockFunction}
        enabledCaptureOptions={config['enabled-capture-options-desktop']}
        allowStillfieFallback={config['web-camera-allow-stillfie-fallback']}
        isProcessing={null}
        currentStep={SelfieStep.Center}
        requireSelfieMatch={selfieMatch}
        skipPromptPage={false}
        uiReady={true}
      />
    </StepSelfieStyleProvider>,
    <StepSelfieStyleProvider
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      value={styles}
      key={`${combinedStepPagePrefix(stepSelfie)}capture-page`}
    >
      <InquirySelfieRecordView
        audioEnabled={false}
        assets={config.assets['record-page']}
        localizations={config.localizations['capture-page']}
        capturedSelfies={[]}
        webCameraManualCaptureDelayMs={config['web-camera-manual-capture-delay-ms']}
        clearCapturedSelfies={mockFunction}
        handleComplete={mockFunction}
        handleFailed={mockFunction}
        handleSlowFaceDetection={mockFunction}
        detectTimeout={DETECT_TIMEOUT}
        requireSelfieMatch={selfieMatch}
        saveCapturedSelfie={mockFunction}
        // disable webcam preview
        videoConstraints={{ deviceId: { exact: 'fake' } }}
        requireHigherAccuracyCapture={false}
        isEditorPreview={true}
        featureFlags={{}}
        mobileExperienceLocalizations={config.localizations['pending-page']}
        mobileExperienceVerticalPosition={config['pending-page-text-vertical-position']}
        enableMobilePendingExperienceForWeb={false}
      />
    </StepSelfieStyleProvider>,
  ];
}

function selfieReflectionStepPages(stepSelfieReflection: StepSelfieReflection): ReactElement[] {
  const mockFunction = (): Promise<void> => Promise.resolve();
  const { config, styles } = stepSelfieReflection;

  return [
    <BaseStepStyleProvider
      value={styles}
      key={`${combinedStepPagePrefix(stepSelfieReflection)}prompt-page`}
    >
      <InquirySelfieReflectionPromptView
        handleStartRecording={mockFunction}
        handleDeviceHandoff={mockFunction}
        enabledCaptureOptions={config['enabled-capture-options-desktop']}
        uiReady={true}
      />
    </BaseStepStyleProvider>,
    <div key='selfie-reflection-record-view'>
      <h1>Selfie Reflection</h1>
      <p>
        This is a placeholder for the Selfie Reflection step. The real widget cannot load in FLE
        previews.
      </p>
    </div>,
  ];
}

function documentStepPages(stepDocument: StepDocument): ReactElement[] {
  const mockFunction = () => {};
  const mockAsyncFunction = () => Promise.resolve();
  const { config, styles } = stepDocument;

  return [
    <StepDocumentStyleProvider
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      value={styles}
      key={`${combinedStepPagePrefix(stepDocument)}prompt-page`}
    >
      <InquiryDocumentsPromptView
        allowedCaptureOptions={config['enabled-capture-options-desktop']}
        acceptedFileTypes={DEFAULT_DOCUMENT_ALLOWED_FILE_TYPES}
        assets={config.assets['prompt-page']}
        documentFiles={[]}
        // Document::DEFAULT_FILE_LIMIT
        documentFileLimit={30}
        handleAddFiles={mockAsyncFunction}
        handleDeviceHandoff={mockFunction}
        handleDrop={mockFunction}
        handleDropRejected={mockFunction}
        handleInputCapture={mockFunction}
        handleRemoveFile={mockAsyncFunction}
        handleSelectCamera={mockAsyncFunction}
        handleSubmit={mockAsyncFunction}
        hideCameraPermissionsDialog={mockFunction}
        isProcessing={undefined}
        loading={false}
        localizations={config.localizations['prompt-page']}
        openCameraPermissionsDialog={mockFunction}
        page={config['start-page']}
        showCameraPermissionsDialog={false}
        submitting={false}
        uploads={[]}
        pendingDeletions={[]}
        skipPromptPage={false}
      />
    </StepDocumentStyleProvider>,
    <StepDocumentStyleProvider
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      value={styles}
      key={`${combinedStepPagePrefix(stepDocument)}capture-page`}
    >
      <InquiryDocumentsCaptureView
        documentFiles={[]}
        // Document::DEFAULT_FILE_LIMIT
        documentFileLimit={30}
        handleCapture={mockAsyncFunction}
        handleError={mockFunction}
        handleFinish={mockFunction}
        localizations={config.localizations['capture-page']}
        // disable webcam preview
        videoConstraints={{ deviceId: { exact: 'fake' } }}
      />
    </StepDocumentStyleProvider>,
    <StepDocumentStyleProvider
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      value={styles}
      key={`${combinedStepPagePrefix(stepDocument)}check-page`}
    >
      <InquiryDocumentsCheckView
        currentUpload={{
          filename: 'document_filename',
          fileType: 'image/jpeg',
          dataUrl: '',
        }}
        currentUploadCaptureMethod={CaptureMethod.Auto}
        localizations={config.localizations['check-page']}
        isProcessing={false}
        handleContinue={mockAsyncFunction}
        handleRetry={mockFunction}
      />
    </StepDocumentStyleProvider>,
    <StepDocumentStyleProvider
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      value={styles}
      key={`${combinedStepPagePrefix(stepDocument)}pending-page`}
    >
      <InquiryDocumentsPendingView
        assets={config.assets['pending-page']}
        localizations={config.localizations['pending-page']}
        textVerticalPosition={config['pending-page-text-vertical-position']}
      />
    </StepDocumentStyleProvider>,
  ];
}

export const getMaxDeviceHandoffOptions = (stepsPreview: StepPreview[]): DeviceHandoffOption[] =>
  Array.from(
    new Set(
      stepsPreview
        .filter((step): step is CombinedStepClientData => {
          if (isCombinedStep(step)) {
            switch (step.type) {
              case UiStepType.Document:
              case UiStepType.GovernmentId:
              case UiStepType.Selfie:
              case UiStepType.SelfieReflection:
                return [
                  ...step.config['enabled-capture-options-desktop'],
                  ...step.config['enabled-capture-options-mobile'],
                ].includes('device_handoff');
              default:
                assertNever(step);
            }
          }
          return false;
        })
        .flatMap((step) => step.config['device-handoff-options']),
    ),
  );

/**
 * TODO: Because device handoff pages are tied to combined step /
 * ButtonDeviceHandoff, we may want to add a preview for each instance of device
 * handoff. Showing all the device handoff pages also allow self-serve
 * configuring of these screens.
 */
export function deviceHandoffPages({
  stepsPreview,
  currentCombinedStepName,
  defaultCountry,
}: {
  stepsPreview: StepPreview[];
  currentCombinedStepName?: string;
  defaultCountry?: string;
}): ReactElement[] {
  const maxDeviceHandoffOptions: DeviceHandoffOption[] = getMaxDeviceHandoffOptions(stepsPreview);
  if (maxDeviceHandoffOptions.length === 0) {
    return [];
  }

  const mockFunction = () => {};
  const combinedSteps = stepsPreview.filter(isCombinedStep);

  // We arbitrarily use the configs on the first encountered step for the preview.
  // TODO: Once we start supporting different copy for each combined step, we should show them all.
  const currentCombinedStep = currentCombinedStepName
    ? (combinedSteps.find((step) => step.name === currentCombinedStepName) ?? combinedSteps[0])
    : combinedSteps[0];
  const { config, styles } = currentCombinedStep;

  let pages: ReactElement[] = [
    <BaseStepStyleProvider key='device-handoff-prompt-page' value={styles}>
      <InquiryDeviceHandoffPromptView
        assets={config?.assets?.['device-handoff-prompt-page']}
        deviceHandoffOptions={maxDeviceHandoffOptions}
        deviceHandoffListStyle={config?.['device-handoff-list-style']}
        localizations={config?.localizations?.['device-handoff-prompt-page']}
        link='https://withpersona.com/'
        shortlink={undefined}
        copyShortlink={() => null}
        handleSmsDeviceHandoff={mockFunction}
        handleEmailDeviceHandoff={mockFunction}
      />
    </BaseStepStyleProvider>,
    <BaseStepStyleProvider key='device-handoff-started-page' value={styles}>
      <InquiryDeviceHandoffStartedView
        assets={config.assets['device-handoff-started-page']}
        localizations={config.localizations['device-handoff-started-page']}
        resumeOnDevice={mockFunction}
      />
    </BaseStepStyleProvider>,
  ];

  if (maxDeviceHandoffOptions.includes(DeviceHandoffOption.Email)) {
    pages = pages.concat([
      <BaseStepStyleProvider key='device-handoff-email-prompt-page' value={styles}>
        <InquiryDeviceHandoffEmailPromptView
          assets={config?.assets?.['device-handoff-email-prompt-page']}
          localizations={config.localizations['device-handoff-email-prompt-page']}
          handleEmailOneTimeLink={mockFunction}
          prefill=''
          disabled={false}
        />
      </BaseStepStyleProvider>,
      <BaseStepStyleProvider key='device-handoff-email-sent-page' value={styles}>
        <InquiryDeviceHandoffEmailSentView
          assets={config?.assets?.['device-handoff-email-sent-page']}
          emailAddress='sample@sample.com'
          localizations={config.localizations['device-handoff-email-sent-page']}
          resendOneTimeLink={mockFunction}
        />
      </BaseStepStyleProvider>,
    ]);
  }

  if (maxDeviceHandoffOptions.includes(DeviceHandoffOption.Sms)) {
    pages = pages.concat([
      <BaseStepStyleProvider key='device-handoff-sms-prompt-page' value={styles}>
        <InquiryDeviceHandoffSmsPromptView
          assets={config?.assets?.['device-handoff-sms-prompt-page']}
          defaultCountry={defaultCountry ?? 'US'}
          localizations={config.localizations['device-handoff-sms-prompt-page']}
          handleSmsOneTimeLink={mockFunction}
          prefill=''
          disabled={false}
        />
      </BaseStepStyleProvider>,
      <BaseStepStyleProvider key='device-handoff-sms-sent-page' value={styles}>
        <InquiryDeviceHandoffSmsSentView
          assets={config?.assets?.['device-handoff-sms-sent-page']}
          localizations={config.localizations['device-handoff-sms-sent-page']}
          phoneNumber='+12344567890'
          resendOneTimeLink={mockFunction}
        />
      </BaseStepStyleProvider>,
    ]);
  }

  return pages;
}
